import { useCallback } from "react";
import { useStore } from "react-redux";
import { useSelector } from "./useSelector";
import { Currency } from "../../types";
import { isFrench } from "../../utils/LocaleUtil";
import AuthService from "../../service/authService";
import { USER_INFO_ACTION_ENUM } from "../reducers/userInfo";
import {
  CURRENCY_STORAGE_ITEM,
  DEFAULT_CURRENCY,
} from "../../constant/common.constant";

export const useCurrency = () => {
  const { dispatch } = useStore();
  const { currency } = useSelector((state) => state.userInfo);

  function getCurrencyFromLocal() {
    return (localStorage.getItem(CURRENCY_STORAGE_ITEM) ||
      DEFAULT_CURRENCY) as Currency;
  }

  /**
   * Currency is also save in the localStorage for backward compatibility
   */
  const setCurrency = useCallback(
    (currency: Currency) => {
      localStorage.setItem(CURRENCY_STORAGE_ITEM, currency ?? DEFAULT_CURRENCY);
      AuthService.updateUserCurrency(currency ?? DEFAULT_CURRENCY);
      dispatch({
        type: USER_INFO_ACTION_ENUM.UPDATE_USER_CURRENCY,
        payload: {
          currency: currency ?? DEFAULT_CURRENCY,
        },
      });
    },
    [dispatch]
  );

  const getCurrency = useCallback((): Currency => {
    if (!currency) {
      const currencyFromLocal = getCurrencyFromLocal();
      setCurrency(currencyFromLocal);
      return currencyFromLocal;
    }
    return currency;
  }, [currency, setCurrency]);

  const setUSD = useCallback(() => {
    setCurrency("usd");
  }, [setCurrency]);

  const setCAD = useCallback(() => {
    setCurrency("cad");
  }, [setCurrency]);

  const isCAD = useCallback(() => {
    return currency === "cad";
  }, [currency]);

  const isUSD = useCallback(() => {
    return currency === "usd";
  }, [currency]);

  const getFullCurrencyFormatter = useCallback(() => {
    return getCurrencyFormatter(currency, "symbol");
  }, [currency]);

  /**
   * Generic currency formatter with currency as input
   * @param {*} currency
   * @returns
   */
  const getCurrencyFormatter = (
    currency: Currency,
    currencyDisplay: any = "narrowSymbol"
  ) => {
    let formatter = undefined;

    const localCurrency = isFrench() ? "fr-CA" : "en-CA";

    //Default is CAD, if currencyState is not yet defined
    if (!currency || currency === "cad") {
      //'en-US' is not an error, is to make sure the current prefix is CA$
      formatter = new Intl.NumberFormat(localCurrency, {
        style: "currency",
        currency: "CAD",
        currencyDisplay: currencyDisplay,
        // These options are needed to round to whole numbers if that's what you want.
        minimumFractionDigits: 2, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
        //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
      });
    } else if (currency === "usd") {
      //'en-CA' is not an error, is to make sure the current prefix is US$
      formatter = Intl.NumberFormat(localCurrency, {
        style: "currency",
        currency: "USD",
        currencyDisplay: currencyDisplay,
        // These options are needed to round to whole numbers if that's what you want.
        minimumFractionDigits: 2, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
        //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
      });
    }

    return formatter;
  };

  const getNarrowCurrencyFormatter = useCallback(() => {
    return getCurrencyFormatter(currency, "narrowSymbol");
  }, [currency]);

  const getCurrencySymbol = useCallback(() => {
    const formatter = getCurrencyFormatter(currency, "code");
    const parts = formatter?.formatToParts(99); // number doesn't matter here
    const currencyIndex = parts?.findIndex((part) => part.type === "currency");

    return parts?.[currencyIndex ?? 0].value;
  }, [currency]);

  const FullCurrencyFormatter = getFullCurrencyFormatter();
  const NarrowCurrencyFormatter = getNarrowCurrencyFormatter();

  return {
    isCAD,
    isUSD,
    setUSD,
    setCAD,
    FullCurrencyFormatter,
    NarrowCurrencyFormatter,
    getCurrency,
    getCurrencySymbol,
    getCurrencyFormatter,
  };
};
